import { 
  KioskSession, 
  KioskSessionDetail, 
  KioskSessionGridRow,
  KioskSessionFilters
} from '../types/kiosk-session-types';
import { 
  SessionsListResponse, 
  SessionDetailResponse 
} from '../types/api-types';
import { fromApiDateFormat, isValidApiDateFormat, fromDisplayDateFormat } from '../utils/date-utils';

/**
 * Data transformer for Kiosk Session API responses
 * Handles conversion between API responses and frontend data models
 * Follows the transformer pattern established in the existing codebase
 */
export class KioskSessionTransformer {
  
  /**
   * Transforms API sessions list response to frontend grid rows
   * Converts date strings and formats data for grid display
   */
  static transformToGridRows(response: SessionsListResponse | any[]): KioskSessionGridRow[] {
    // Handle raw array response
    if (Array.isArray(response)) {
      return response
        .map(item => this.transformRawApiItemToGridRow(item))
        .filter(row => row !== null) as KioskSessionGridRow[];
    }

    // Handle OData wrapper response
    if (!response.isSuccess || !response.rows) {
      return [];
    }

    return response.rows
      .map(session => this.transformSessionToGridRow(session))
      .filter(row => row !== null) as KioskSessionGridRow[];
  }

  /**
   * Transforms raw API item to grid row format
   * Handles the actual API response structure
   */
  static transformRawApiItemToGridRow(item: any): KioskSessionGridRow | null {
    try {
      return {
        session_id: item.id || item.cdrId || 'N/A',
        status: this.mapSessionStatus(item.status),
        evse_id: item.evseId || 'Unknown',
        connector_id: item.connectorId || 'Unknown',
        cdr_id: item.cdrId || '',
        start_time: this.parseApiDate(item.startTime),
        stop_time: this.parseApiDate(item.endTime),
        total_energy: this.sanitizeNumericValue(item.energy, 0),
        settlement_value: this.sanitizeNumericValue(item.cost, 0),
        last_4_digits: this.sanitizeNumericValue(item.token?.slice(-4), 0),
        actor_hierarchy: item.actorHierarchy || item.actor_hierarchy,
        machine_name: item.machineName || item.machine_name,
        payment_transaction_id: item.transactionId || item.paymentTransactionId || item.payment_transaction_id,
        locationId: item.locationId || item.location_id, // Preserve locationId for enrichment
        // NEW GRID FIELDS (HIVE-6390)
        auth_method: this.mapAuthMethod(item.authMethod),
        connector_standard: this.mapConnectorStandard(item.connectorStandard),
        max_power: item.maxPower != null ? this.convertWattsToKilowatts(item.maxPower) : undefined,
        power_type: this.mapPowerType(item.powerType),
        location_name: item.locationName || item.location_name,
        currency: item.currency,
        duration: this.calculateDuration(item.startTime, item.endTime),
        // NEW PAYMENT TRANSACTION FIELDS (HIVE-6397)
        authAmount: item.authAmount != null ? this.sanitizeNumericValue(item.authAmount, 0) : undefined,
        settAmount: item.settAmount != null ? this.sanitizeNumericValue(item.settAmount, 0) : undefined,
        authTime: item.authTime ? this.parseApiDate(item.authTime) : undefined,
        settTime: item.settTime ? this.parseApiDate(item.settTime) : undefined,
        totalIncVat: item.totalIncVat != null ? this.sanitizeNumericValue(item.totalIncVat, 0) : undefined,
        totalExcVat: item.totalExcVat != null ? this.sanitizeNumericValue(item.totalExcVat, 0) : undefined,
        vatAmount: item.vatAmount != null
          ? this.sanitizeNumericValue(item.vatAmount, 0)
          : (item.totalIncVat != null && item.totalExcVat != null)
            ? this.sanitizeNumericValue(item.totalIncVat, 0) - this.sanitizeNumericValue(item.totalExcVat, 0)
            : undefined,
        paymentMethod: this.mapPaymentMethod(item.paymentMethod),
        cardIdentifier: item.cardIdentifier || undefined,
        connector_alias: item.connectorAlias || item.connector_alias,
        receipt: item.receipt || undefined,
        plate: item.plate?.toUpperCase() || undefined,
        phone_number: item.phoneNumber || undefined
      } as any;
    } catch (error) {
      console.warn(`Failed to transform raw API item ${item.id}:`, error);
      return null;
    }
  }

  /**
   * Transforms a single session to grid row format
   * Handles date parsing and validation
   */
  static transformSessionToGridRow(session: KioskSession): KioskSessionGridRow | null {
    // Validate required fields - session_id is mandatory
    if (!session.session_id || session.session_id.trim() === '') {
      return null;
    }

    try {
      return {
        session_id: session.session_id,
        status: session.status || 'UNKNOWN',
        evse_id: session.evse_id || 'Unknown',
        connector_id: session.connector_id || 'Unknown',
        cdr_id: session.cdr_id || '',
        start_time: this.parseApiDate(session.start_time),
        stop_time: this.parseApiDate(session.stop_time),
        total_energy: this.sanitizeNumericValue(session.total_energy, 0),
        settlement_value: this.sanitizeNumericValue(session.settlement_value, 0),
        last_4_digits: this.sanitizeNumericValue(session.last_4_digits, 0),
        actor_hierarchy: (session as any).actor_hierarchy,
        machine_name: (session as any).machine_name,
        payment_transaction_id: (session as any).payment_transaction_id,
        // NEW GRID FIELDS (HIVE-6390)
        auth_method: session.auth_method,
        connector_standard: session.connector_standard,
        max_power: session.max_power,
        power_type: session.power_type,
        location_name: session.location_name,
        currency: session.currency,
        cardIdentifier: (session as any).cardIdentifier || undefined,
        plate: session.plate || undefined,
        phone_number: session.phone_number || undefined,
      };
    } catch (error) {
      console.warn(`Failed to transform session ${session.session_id}:`, error);
      return null;
    }
  }

  /**
   * Transforms API session detail response to enhanced detail model
   * Adds computed formatting fields for UI display
   */
  static transformToSessionDetail(response: SessionDetailResponse | any): KioskSessionDetail | null {
    // Handle raw API response (single object)
    if (Array.isArray(response)) {
      if (response.length === 0) return null;
      return this.transformRawApiItemToDetail(response[0]);
    }

    // Handle direct object response
    if (response && !response.isSuccess && !response.rows) {
      return this.transformRawApiItemToDetail(response);
    }

    // Handle OData wrapper response
    if (!response.isSuccess || !response.rows || response.rows.length === 0) {
      return null;
    }

    const session = response.rows[0];
    
    try {
      const detail: KioskSessionDetail = {
        ...session,
        // Add computed formatting fields
        formattedStartTime: this.formatDateTime(session.start_time),
        formattedStopTime: this.formatDateTime(session.stop_time),
        formattedDuration: this.formatDuration(session.duration),
        formattedEnergy: this.formatEnergy(session.total_energy),
        formattedValue: this.formatCurrency(session.settlement_value)
      };

      return detail;
    } catch (error) {
      console.error(`Failed to transform session detail ${session.session_id}:`, error);
      return null;
    }
  }

  /**
   * Transforms raw API item to session detail format
   * Updated to include new API fields from ApiSessionInfo (HIVE-6390)
   */
  static transformRawApiItemToDetail(item: any): KioskSessionDetail | null {
    try {
      const detail: KioskSessionDetail = {
        // Basic session info
        session_id: item.id || item.cdrId || 'N/A',
        status: this.mapSessionStatus(item.status),
        evse_id: item.evse || item.evseId || 'Unknown',
        connector_id: item.connnector || item.connectorId || 'Unknown', // Note: API has typo "connnector"
        cdr_id: item.cdrId || '',
        start_time: item.startTime || '',
        stop_time: item.endTime || '',
        total_energy: this.sanitizeNumericValue(item.energy, 0),
        settlement_value: this.sanitizeNumericValue(item.cost, 0),
        last_4_digits: this.sanitizeNumericValue(item.token?.slice(-4), 0),

        // Detail-specific fields
        location_id: item.location || item.locationId || '',
        location_name: item.locationName || 'Unknown Location',
        duration: this.calculateDuration(item.startTime, item.endTime),
        auth_time: item.startTime || '',
        settlement_time: item.endTime || '',
        machine_id: item.machineId || 0,
        connector_type: this.mapConnectorStandard(item.connectorStandard),
        updatedDT: '',
        tariff_id: 0,
        description: `Session ${this.mapSessionStatus(item.status)}`,
        price_components: 0,
        valid_from: '',
        valid_to: '',
        restrictions: 'No restrictions',

        // Computed formatting fields
        formattedStartTime: this.formatDateTime(item.startTime),
        formattedStopTime: this.formatDateTime(item.endTime),
        formattedDuration: this.calculateDuration(item.startTime, item.endTime),
        formattedEnergy: this.formatEnergy(this.sanitizeNumericValue(item.energy, 0)),
        formattedValue: this.formatCurrency(this.sanitizeNumericValue(item.cost, 0), item.currency),

        // ===== NEW FIELDS FROM API (HIVE-6390) =====

        // Authentication & Token Info
        token: item.token || undefined,
        token_type: item.tokeType || item.tokenType || undefined, // Note: API has typo "tokeType"
        auth_method: this.mapAuthMethod(item.authMethod),
        state: this.mapSessionState(item.state),
        remark: item.remark || undefined,
        plate: item.plate?.toUpperCase() || undefined,
        phone_number: item.phoneNumber || undefined,

        // Payment & Receipt Info
        payment_id: item.paymentId || undefined,
        receipt: item.receipt || undefined,
        currency: item.currency || undefined,

        // Cost Breakdown
        fixed_cost: item.fixedCost != null ? this.sanitizeNumericValue(item.fixedCost, 0) : undefined,
        energy_cost: item.energyCost != null ? this.sanitizeNumericValue(item.energyCost, 0) : undefined,
        time_cost: item.timeCost != null ? this.sanitizeNumericValue(item.timeCost, 0) : undefined,
        parking_cost: item.parkingCost != null ? this.sanitizeNumericValue(item.parkingCost, 0) : undefined,
        reservation_cost: item.reservationCost != null ? this.sanitizeNumericValue(item.reservationCost, 0) : undefined,

        // Connector Details
        connector_standard: this.mapConnectorStandard(item.connectorStandard),
        connector_format: this.mapConnectorFormat(item.connectorFormat),
        max_power: item.maxPower != null ? this.convertWattsToKilowatts(item.maxPower) : undefined,
        connector_alias: item.connectorAlias || undefined,
        power_type: this.mapPowerType(item.powerType),

        // Time Details
        total_time: item.totalTime != null ? this.sanitizeNumericValue(item.totalTime, 0) : undefined,
        total_parking_time: item.totalParkingTime != null ? this.sanitizeNumericValue(item.totalParkingTime, 0) : undefined,

        // Location Details
        address: item.address || undefined,
        city: item.city || undefined,
        country: item.country || undefined,
        postal_code: item.postalCode || undefined,
        latitude: item.latitude || undefined,
        longitude: item.longitude || undefined,
        timezone: item.timezone || undefined,
        country_code: item.countryCode || undefined,
        party_id: item.partyId || undefined,

        // Identifiers
        operator_id: item.operatorId != null ? this.sanitizeNumericValue(item.operatorId, 0) : undefined,
        transaction_id: item.transactionId != null ? this.sanitizeNumericValue(item.transactionId, 0) : undefined,
        site_id: item.siteId != null ? this.sanitizeNumericValue(item.siteId, 0) : undefined,

        // ===== NEW PAYMENT TRANSACTION FIELDS (HIVE-6397) =====
        authAmount: item.authAmount != null ? this.sanitizeNumericValue(item.authAmount, 0) : undefined,
        settAmount: item.settAmount != null ? this.sanitizeNumericValue(item.settAmount, 0) : undefined,
        authTime: item.authTime || undefined,
        settTime: item.settTime || undefined,
        totalIncVat: item.totalIncVat != null ? this.sanitizeNumericValue(item.totalIncVat, 0) : undefined,
        totalExcVat: item.totalExcVat != null ? this.sanitizeNumericValue(item.totalExcVat, 0) : undefined,
        vatAmount: item.vatAmount != null
          ? this.sanitizeNumericValue(item.vatAmount, 0)
          : (item.totalIncVat != null && item.totalExcVat != null)
            ? this.sanitizeNumericValue(item.totalIncVat, 0) - this.sanitizeNumericValue(item.totalExcVat, 0)
            : undefined,
        cardIdentifier: item.cardIdentifier || undefined,
        machineName: item.machineName || undefined,
      };

      return detail;
    } catch (error) {
      console.error(`Failed to transform raw API item to detail ${item.id}:`, error);
      return null;
    }
  }

  /**
   * Maps API session status enum (0-6) to display string
   */
  private static mapSessionStatus(status: number | string | undefined): string {
    if (typeof status === 'string') return status;
    const statusMap: Record<number, string> = {
      0: 'ACTIVE',
      1: 'PENDING',
      2: 'COMPLETED',
      3: 'INVALID',
      4: 'PENDING',
      5: 'RESERVATION',
      6: 'UNKNOWN'
    };
    return statusMap[status as number] || 'UNKNOWN';
  }

  /**
   * Maps API auth method enum (0-3) to display string
   */
  private static mapAuthMethod(authMethod: number | string | undefined): 'AUTH_REQUEST' | 'COMMAND' | 'WHITELIST' | 'UNKNOWN' {
    if (typeof authMethod === 'string') return authMethod as any;
    const methodMap: Record<number, 'AUTH_REQUEST' | 'COMMAND' | 'WHITELIST' | 'UNKNOWN'> = {
      0: 'AUTH_REQUEST',
      1: 'COMMAND',
      2: 'WHITELIST',
      3: 'UNKNOWN'
    };
    return methodMap[authMethod as number] || 'UNKNOWN';
  }

  /**
   * Maps API session state enum (0-7) to display string
   */
  private static mapSessionState(state: number | string | undefined): 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'INVALID' | 'UNKNOWN' {
    if (typeof state === 'string') return state as any;
    const stateMap: Record<number, 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'INVALID' | 'UNKNOWN'> = {
      0: 'PENDING',
      1: 'ACTIVE',
      2: 'COMPLETED',
      3: 'INVALID',
      4: 'PENDING',
      5: 'ACTIVE',
      6: 'COMPLETED',
      7: 'UNKNOWN'
    };
    return stateMap[state as number] || 'UNKNOWN';
  }

  /**
   * Maps API connector standard enum to display string
   */
  private static mapConnectorStandard(standard: number | string | undefined): string {
    if (typeof standard === 'string') return standard;
    if (standard == null) return 'Unknown';
    const standardMap: Record<number, string> = {
      0: 'CHADEMO',
      1: 'CHAOJI',
      24: 'IEC_62196_T1',
      25: 'IEC_62196_T1_COMBO',
      26: 'IEC_62196_T2',
      27: 'IEC_62196_T2_COMBO',
      // Add more mappings as needed
    };
    return standardMap[standard] || `Type ${standard}`;
  }

  /**
   * Maps API connector format enum (0-1) to display string
   */
  private static mapConnectorFormat(format: number | string | undefined): 'SOCKET' | 'CABLE' | 'UNKNOWN' {
    if (typeof format === 'string') return format as any;
    const formatMap: Record<number, 'SOCKET' | 'CABLE' | 'UNKNOWN'> = {
      0: 'SOCKET',
      1: 'CABLE'
    };
    return formatMap[format as number] || 'UNKNOWN';
  }

  /**
   * Maps API power type enum (0-4) to display string
   */
  private static mapPowerType(powerType: number | string | undefined): 'AC_1_PHASE' | 'AC_2_PHASE' | 'AC_2_PHASE_SPLIT' | 'AC_3_PHASE' | 'DC' | 'UNKNOWN' {
    if (typeof powerType === 'string') return powerType as any;
    const powerMap: Record<number, 'AC_1_PHASE' | 'AC_2_PHASE' | 'AC_2_PHASE_SPLIT' | 'AC_3_PHASE' | 'DC' | 'UNKNOWN'> = {
      0: 'AC_1_PHASE',
      1: 'AC_2_PHASE',
      2: 'AC_2_PHASE_SPLIT',
      3: 'AC_3_PHASE',
      4: 'DC'
    };
    return powerMap[powerType as number] || 'UNKNOWN';
  }

  /**
   * Maps API payment method enum (1-2) to display string
   */
  private static mapPaymentMethod(paymentMethod: number | string | undefined): string {
    if (paymentMethod == null) return 'Unknown';

    // Convert string numbers to integers
    const method = typeof paymentMethod === 'string' ? parseInt(paymentMethod, 10) : paymentMethod;

    // If it's NaN or not a number, return Unknown
    if (isNaN(method)) return 'Unknown';

    const paymentMap: Record<number, string> = {
      1: 'Credit Card',
      2: 'Prepaid'
    };
    return paymentMap[method] || 'Unknown';
  }

  /**
   * Calculate duration string from start and end times
   */
  private static calculateDuration(startTime?: string, endTime?: string): string {
    if (!startTime || !endTime) return '';

    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end.getTime() - start.getTime();

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } catch {
      return '00:00:00';
    }
  }

  /**
   * Converts power from Watts to kilowatts
   * API returns power in Watts, UI displays in kW
   */
  private static convertWattsToKilowatts(watts: number): number {
    if (typeof watts !== 'number' || isNaN(watts)) {
      return 0;
    }
    return watts / 1000;
  }

  /**
   * Validates and sanitizes API response data
   * Ensures data integrity and handles missing fields
   */
  static validateAndSanitizeSession(session: Partial<KioskSession>): KioskSession | null {
    // Check for required fields
    if (!session.session_id) {
      console.warn('Session missing required fields:', session);
      return null;
    }

    return {
      // Main grid columns with defaults
      session_id: session.session_id,
      status: session.status || 'UNKNOWN',
      evse_id: session.evse_id || 'Unknown',
      connector_id: session.connector_id || 'Unknown',
      cdr_id: session.cdr_id || '',
      start_time: session.start_time || '',
      stop_time: session.stop_time || '',
      total_energy: this.sanitizeNumericValue(session.total_energy, 0),
      settlement_value: this.sanitizeNumericValue(session.settlement_value, 0),
      
      // Detail fields with defaults
      location_id: session.location_id || '',
      location_name: session.location_name || 'Unknown Location',
      duration: session.duration || '00:00:00',
      last_4_digits: this.sanitizeNumericValue(session.last_4_digits, 0),
      auth_time: session.auth_time || '',
      settlement_time: session.settlement_time || '',
      machine_id: this.sanitizeNumericValue(session.machine_id, 0),
      connector_type: session.connector_type || 'Unknown',
      updatedDT: session.updatedDT || '',
      tariff_id: this.sanitizeNumericValue(session.tariff_id, 0),
      description: session.description || 'No description',
      price_components: this.sanitizeNumericValue(session.price_components, 0),
      valid_from: session.valid_from || '',
      valid_to: session.valid_to || '',
      restrictions: session.restrictions || 'No restrictions'
    };
  }

  /**
   * Parses API date format (DD.MM.YYYY or YYYY-MM-DD) to Date object
   * Handles invalid dates gracefully
   */
  private static parseApiDate(dateString: string): Date | null {
    if (!dateString) {
      return null;
    }

    try {
      // Check for DD.MM.YYYY or DD/MM/YYYY format first (display format)
      if (dateString.includes('.') || (dateString.includes('/') && dateString.indexOf('/') < 3)) {
        try {
          return fromDisplayDateFormat(dateString);
        } catch {
          // Fall through to other formats
        }
      }
      
      // Check for YYYY-MM-DD format (API format)
      if (isValidApiDateFormat(dateString)) {
        return fromApiDateFormat(dateString);
      }
      
      // Try parsing as ISO date string fallback
      // API returns UTC dates without timezone suffix — append 'Z' to ensure UTC parsing
      let normalized = dateString;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateString) && !dateString.endsWith('Z') && !dateString.includes('+')) {
        normalized = dateString + 'Z';
      }
      const parsedDate = new Date(normalized);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
      
      console.warn(`Invalid date format: ${dateString}`);
      return null;
    } catch (error) {
      console.warn(`Date parsing failed for ${dateString}:`, error);
      return null;
    }
  }

  /**
   * Sanitizes numeric values and provides defaults
   */
  private static sanitizeNumericValue(value: unknown, defaultValue: number): number {
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    return defaultValue;
  }

  /**
   * Formats date/time for display
   */
  private static formatDateTime(dateString: string): string {
    try {
      const date = this.parseApiDate(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString || 'Invalid date';
    }
  }

  /**
   * Formats duration string for display
   */
  private static formatDuration(duration: string): string {
    if (!duration) return '00:00:00';
    
    // Ensure HH:MM:SS format
    const parts = duration.split(':');
    if (parts.length >= 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      const seconds = parts[2]?.padStart(2, '0') || '00';
      return `${hours}:${minutes}:${seconds}`;
    }
    
    return duration;
  }

  /**
   * Formats energy value for display
   */
  private static formatEnergy(energy: number): string {
    if (typeof energy !== 'number' || isNaN(energy)) {
      return '0.0 kWh';
    }
    
    return `${energy.toFixed(1)} kWh`;
  }

  /**
   * Formats currency value for display
   * Uses actual currency from API, validates ISO 4217, falls back to ILS
   */
  private static formatCurrency(value: number, currency: string = 'ILS'): string {
    if (typeof value !== 'number' || isNaN(value)) {
      return '₪0.00';
    }

    const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : 'ILS';
    return new Intl.NumberFormat('en-IL', {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Formats payment method code to readable string
   * @param method - Payment method code (CC, PP, etc.)
   * @returns Human-readable payment method name
   */
  private static formatPaymentMethod(method?: string): string {
    if (!method) return 'N/A';

    switch (method.toUpperCase()) {
      case 'CC': return 'Credit Card';
      case 'PP': return 'PayPal';
      default: return method; // Return original value if unknown
    }
  }

  /**
   * Applies client-side filtering for search and last4Digits
   * Used when API doesn't support these filter parameters
   */
  static applyClientSideFilters(
    sessions: KioskSessionGridRow[], 
    filters: KioskSessionFilters
  ): KioskSessionGridRow[] {
    let filtered = [...sessions];

    // Apply search filter across multiple fields: sessionId, status, cdrId, evseId
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(session =>
        session.session_id.toLowerCase().includes(searchLower) ||
        session.status.toLowerCase().includes(searchLower) ||
        session.cdr_id.toLowerCase().includes(searchLower) ||
        session.evse_id.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter (multi-select)
    if (filters.statuses?.length) {
      filtered = filtered.filter(session =>
        filters.statuses!.includes(session.status?.toUpperCase() ?? '')
      );
    }

    // Apply last 4 digits filter - match against cardIdentifier (displayed in UI as ****XXXX)
    if (filters.last4Digits) {
      filtered = filtered.filter(session => {
        if (session.cardIdentifier) {
          return session.cardIdentifier.includes(filters.last4Digits!);
        }
        if (session.last_4_digits) {
          return session.last_4_digits.toString().includes(filters.last4Digits!);
        }
        return false;
      });
    }

    return filtered;
  }

  /**
   * Handles API error responses and provides user-friendly messages
   */
  static transformApiError(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error && typeof error === 'object') {
      if ('errorMessage' in error && typeof (error as { errorMessage: unknown }).errorMessage === 'string') {
        return (error as { errorMessage: string }).errorMessage;
      }
      
      if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
        return (error as { message: string }).message;
      }
    }
    
    return 'An unexpected error occurred while processing session data';
  }
}